/**
 * Kategorize Ürün Galerisi — Custom Element
 *
 * Davranış:
 *  - Tab tıklama: aktif kategori değişir
 *  - Aktif kategoriye uymayan thumbnail'ler [hidden] olur
 *  - Aktif kategorinin ilk thumbnail'i seçilir, ana fotoğraf değişir
 *  - "Tümü" tab'ı: tüm thumbnail'ler görünür
 *  - Thumbnail tıklama: ana fotoğrafı değiştirir (kategori değiştirmez)
 *
 * Lightbox: tema'nın mevcut lightbox sistemi kullanılır (tüm fotoğraflarda dolaşır).
 * Bu JS lightbox'a dokunmaz.
 */
(function () {
  if (customElements.get('product-categorized-gallery')) return;

  class ProductCategorizedGallery extends HTMLElement {
    connectedCallback() {
      this.tabBar = this.querySelector('[data-tab-bar]');
      this.mainContainer = this.querySelector('[data-main-image-container]');
      this.thumbStrip = this.querySelector('[data-thumb-strip]');

      if (!this.tabBar || !this.mainContainer || !this.thumbStrip) {
        console.warn('[product-categorized-gallery] missing required child elements');
        return;
      }

      this.tabs = Array.from(this.tabBar.querySelectorAll('[role="tab"]'));
      this.mainItems = Array.from(this.mainContainer.querySelectorAll('[data-media-id]'));
      this.thumbs = Array.from(this.thumbStrip.querySelectorAll('[data-media-id]'));

      this.tabBar.addEventListener('click', this.onTabClick.bind(this));
      this.tabBar.addEventListener('keydown', this.onTabKeyDown.bind(this));
      this.thumbStrip.addEventListener('click', this.onThumbClick.bind(this));

      // Varyant değişince varyantın fotoğrafını otomatik göster.
      // variant-picker, on:variant:change olayını .js-product section'ına
      // bubble eder. Galeri dinamik içerik bölgesinde DEĞİL (varyant
      // değişiminde DOM'u korunur), bu yüzden olayı kendisi dinlemeli —
      // varsayılan media-gallery.js'in yaptığı gibi.
      this.section = this.closest('.js-product');
      if (this.section) {
        this.variantChangeHandler = this.onVariantChange.bind(this);
        this.section.addEventListener('on:variant:change', this.variantChangeHandler);
      }
    }

    disconnectedCallback() {
      if (this.section && this.variantChangeHandler) {
        this.section.removeEventListener('on:variant:change', this.variantChangeHandler);
      }
    }

    onTabClick(event) {
      const tab = event.target.closest('[role="tab"]');
      if (!tab) return;
      this.activateCategory(tab.dataset.category);
    }

    onTabKeyDown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const current = this.tabs.indexOf(document.activeElement);
      if (current < 0) return;
      const next = event.key === 'ArrowRight'
        ? (current + 1) % this.tabs.length
        : (current - 1 + this.tabs.length) % this.tabs.length;
      this.tabs[next].focus();
      this.activateCategory(this.tabs[next].dataset.category);
      event.preventDefault();
    }

    onThumbClick(event) {
      const thumb = event.target.closest('[data-media-id]');
      if (!thumb) return;
      this.showMedia(thumb.dataset.mediaId);
    }

    activateCategory(category, preferredMediaId) {
      if (!category) return;
      this.dataset.activeCategory = category;

      // Tab states
      let activeTab = null;
      this.tabs.forEach((t) => {
        const isActive = t.dataset.category === category;
        t.classList.toggle('is-active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) activeTab = t;
      });

      // Aktif tab'ı bar içinde ortala (komşu tab'lar da görünür kalsın)
      if (activeTab) {
        const bar = this.tabBar;
        const barRect = bar.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        const target = bar.scrollLeft + (tabRect.left - barRect.left) - (barRect.width - tabRect.width) / 2;
        bar.scrollTo({ left: target, behavior: 'smooth' });
      }

      // Thumb filter
      this.thumbs.forEach((th) => {
        const show = category === 'all' || th.dataset.category === category;
        th.hidden = !show;
      });

      // Otomatik seçim: tercih edilen medya (örn. varyant fotoğrafı) görünürse
      // onu seç, yoksa kategorinin ilk görünür thumbnail'ini.
      let toSelect = null;
      if (preferredMediaId) {
        toSelect = this.thumbs.find((th) => !th.hidden && th.dataset.mediaId === preferredMediaId);
      }
      if (!toSelect) toSelect = this.thumbs.find((th) => !th.hidden);
      if (toSelect) this.showMedia(toSelect.dataset.mediaId);
    }

    showMedia(mediaId) {
      // Ana fotoğraf
      this.mainItems.forEach((item) => {
        item.hidden = item.dataset.mediaId !== mediaId;
      });
      // Thumbnail aria-current
      this.thumbs.forEach((th) => {
        if (th.dataset.mediaId === mediaId) {
          th.setAttribute('aria-current', 'true');
        } else {
          th.removeAttribute('aria-current');
        }
      });
      // Custom event — tema'nın lightbox veya başka bir sistemine entegre olabilir
      this.dispatchEvent(new CustomEvent('media:change', {
        detail: { mediaId },
        bubbles: true,
      }));
    }

    /**
     * Varyant değişiminde varyantın atanmış fotoğrafını ana görsel yapar.
     * Varsayılan media-gallery.js de aynı davranışı sergiler; kategori tab'ları
     * eklenince bu özellik kaybolmuştu. featured_media atanmamış varyantlarda
     * (örn. beden/adet) hiçbir şey yapmaz — eski davranışla aynı.
     * @param {CustomEvent} event - variant-picker'ın on:variant:change olayı
     */
    onVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant || !variant.featured_media) return;

      const mediaId = String(variant.featured_media.id);
      const exists = this.thumbs.some((th) => th.dataset.mediaId === mediaId);
      if (!exists) return;

      // Varyant fotoğrafı herhangi bir kategoride olabilir; "Tümü"ye dönüp
      // göster ki thumbnail görünür ve seçili (aria-current) kalsın.
      this.activateCategory('all', mediaId);
    }
  }

  customElements.define('product-categorized-gallery', ProductCategorizedGallery);
})();
